import React, { useState, useEffect } from 'react';
import { FaChartPie, FaChartBar, FaTasks, FaCheckCircle, FaClock } from 'react-icons/fa';
import api from '../../../services/api';

const TaskAnalytics = ({ tasks, statuses, priorities }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/portal/tasks/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateLocalStats = () => {
    const total = tasks.length;
    const completed = tasks.filter(t =>
      statuses.find(s => s.id === t.status_id)?.is_completed_state
    ).length;
    const overdue = tasks.filter(t =>
      t.due_date && new Date(t.due_date) < new Date() &&
      !statuses.find(s => s.id === t.status_id)?.is_completed_state
    ).length;

    return { total, completed, overdue };
  };

  const localStats = calculateLocalStats();

  if (loading) {
    return <div className="analytics-loading">Loading analytics...</div>;
  }

  return (
    <div className="task-analytics">
      {/* Summary Cards */}
      <div className="analytics-cards">
        <div className="analytics-card">
          <div className="card-icon" style={{ backgroundColor: '#3b82f6' }}>
            <FaTasks />
          </div>
          <div className="card-content">
            <h3>Total Tasks</h3>
            <p className="card-value">{localStats.total}</p>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-icon" style={{ backgroundColor: '#10b981' }}>
            <FaCheckCircle />
          </div>
          <div className="card-content">
            <h3>Completed</h3>
            <p className="card-value">{localStats.completed}</p>
            <p className="card-subtitle">
              {localStats.total > 0
                ? `${((localStats.completed / localStats.total) * 100).toFixed(1)}%`
                : '0%'}
            </p>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-icon" style={{ backgroundColor: '#ef4444' }}>
            <FaClock />
          </div>
          <div className="card-content">
            <h3>Overdue</h3>
            <p className="card-value">{localStats.overdue}</p>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-icon" style={{ backgroundColor: '#f59e0b' }}>
            <FaChartBar />
          </div>
          <div className="card-content">
            <h3>Completion Rate</h3>
            <p className="card-value">
              {analytics?.completion_stats?.completion_rate || 0}%
            </p>
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="analytics-section">
        <h3><FaChartPie /> Tasks by Status</h3>
        <div className="status-distribution">
          {analytics?.status_distribution?.map(status => (
            <div key={status.name} className="status-bar-container">
              <div className="status-bar-label">
                <span className="status-name">{status.name}</span>
                <span className="status-count">{status.count}</span>
              </div>
              <div className="status-bar-track">
                <div
                  className="status-bar-fill"
                  style={{
                    width: `${(status.count / localStats.total) * 100}%`,
                    backgroundColor: status.color
                  }}
                />
              </div>
            </div>
          )) || <p>No data available</p>}
        </div>
      </div>

      {/* Priority Distribution */}
      <div className="analytics-section">
        <h3><FaChartBar /> Tasks by Priority</h3>
        <div className="priority-distribution">
          {analytics?.priority_distribution?.map(priority => (
            <div key={priority.name} className="priority-item">
              <div
                className="priority-badge"
                style={{ backgroundColor: priority.color }}
              >
                {priority.name}
              </div>
              <div className="priority-count">{priority.count} tasks</div>
            </div>
          )) || <p>No data available</p>}
        </div>
      </div>

      {/* Additional Stats */}
      {analytics?.completion_stats && (
        <div className="analytics-section">
          <h3>Completion Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <label>Total Completed:</label>
              <span>{analytics.completion_stats.completed_count}</span>
            </div>
            <div className="stat-item">
              <label>Total Tasks:</label>
              <span>{analytics.completion_stats.total_count}</span>
            </div>
            <div className="stat-item">
              <label>Avg Completion Time:</label>
              <span>{analytics.avg_completion_days || 0} days</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskAnalytics;
